import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  Sparkles,
  Award,
  Globe2,
  BookOpenCheck,
  Compass,
  DownloadCloud,
  ShieldCheck,
  FileText,
  Mic,
  Search,
  Bell,
  BarChart3,
  Layers,
  Cpu,
  Database,
  Braces,
  Mail,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1622470190232-81df3782484b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxwcmVzdGlnaW91cyUyMHVuaXZlcnNpdHklMjBhcmNoaXRlY3R1cmUlMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg3NjcyNDM4fDA&ixlib=rb-4.1.0&q=85";
const LIB_IMG =
  "https://images.unsplash.com/photo-1721702754494-fdd7189f946c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcHJvZmVzc29ycyUyMHJlc2VhcmNoaW5nJTIwdW5pdmVyc2l0eSUyMGxpYnJhcnl8ZW58MHx8fHwxNzg3NjcyNDM4fDA&ixlib=rb-4.1.0&q=85";

const AGENCIES = [
  "NSF", "NIH", "ERC", "Wellcome Trust", "DFG", "UKRI",
  "Templeton", "Gates Foundation", "MacArthur",
  "Simons", "JSPS", "Sloan", "ARC", "NRF", "CIHR", "ANR", "Ford",
  "Horizon Europe", "Fulbright",
];

const INDIAN_AGENCIES = [
  // Verified reachable
  { name: "SERB", full: "Science & Engineering Research Board", url: "https://serb.gov.in/" },
  { name: "DST", full: "Department of Science & Technology", url: "https://dst.gov.in/" },
  { name: "CSIR", full: "Council of Scientific & Industrial Research", url: "https://www.csir.res.in/" },
  { name: "ICAR", full: "Indian Council of Agricultural Research", url: "https://icar.org.in/" },
  { name: "ICMR", full: "Indian Council of Medical Research", url: "https://www.icmr.gov.in/" },
  { name: "ICSSR", full: "Indian Council of Social Science Research", url: "https://icssr.org/" },
  { name: "INSA", full: "Indian National Science Academy", url: "https://www.insaindia.res.in/" },
  { name: "ISRO", full: "Indian Space Research Organisation", url: "https://www.isro.gov.in/" },
  { name: "DRDO", full: "Defence Research & Development Organisation", url: "https://drdo.gov.in/" },
  { name: "BRNS", full: "Board of Research in Nuclear Sciences", url: "https://brns.res.in/" },
  { name: "DAE", full: "Department of Atomic Energy", url: "https://dae.gov.in/" },
  { name: "NBHM", full: "National Board for Higher Mathematics", url: "https://www.nbhm.dae.gov.in/" },
  { name: "UGC", full: "University Grants Commission", url: "https://www.ugc.gov.in/" },
  { name: "MeitY", full: "Ministry of Electronics & IT", url: "https://www.meity.gov.in/" },
  { name: "MoES", full: "Ministry of Earth Sciences", url: "https://www.moes.gov.in/" },
  { name: "MoEF", full: "Ministry of Environment & Forests", url: "https://moef.gov.in/" },
  { name: "MoHFW", full: "Ministry of Health & Family Welfare", url: "https://mohfw.gov.in/" },
  { name: "MoP", full: "Ministry of Power", url: "https://powermin.gov.in/" },
  { name: "MoRD", full: "Ministry of Rural Development", url: "https://rural.gov.in/" },
  { name: "MSME", full: "Ministry of MSME (Small Scale)", url: "https://msme.gov.in/" },
  { name: "Textiles", full: "Ministry of Textiles", url: "https://texmin.gov.in/" },
  { name: "MoHUA", full: "Ministry of Housing & Urban Affairs", url: "https://mohua.gov.in/" },
  { name: "Jal Shakti", full: "Ministry of Jal Shakti / Water Resources", url: "https://jalshakti-dowr.gov.in/" },
  { name: "Chem & Petro", full: "Ministry of Chemicals & Petrochemicals", url: "https://chemicals.gov.in/" },
  { name: "PCRA", full: "Petroleum Conservation Research Association", url: "https://www.pcra.org/" },
  { name: "TIFR", full: "Tata Institute of Fundamental Research", url: "https://www.tifr.res.in/" },
  { name: "EU", full: "European Union (India partnerships)", url: "https://european-union.europa.eu/" },
  // Reachable-from-India (may be region-restricted from our sandbox check — canonical links)
  { name: "DBT", full: "Department of Biotechnology", url: "https://dbtindia.gov.in/" },
  { name: "AICTE", full: "All India Council for Technical Education", url: "https://www.aicte-india.org/" },
  { name: "AERB", full: "Atomic Energy Regulatory Board", url: "https://www.aerb.gov.in/" },
  { name: "MoPNG", full: "Ministry of Petroleum & Natural Gas", url: "https://mopng.gov.in/" },
  { name: "MoD", full: "Ministry of Defence", url: "https://www.mod.gov.in/" },
  { name: "Rly", full: "Ministry of Railways", url: "https://www.indianrailways.gov.in/railwayboard/" },
  { name: "ARDB", full: "Aeronautics R&D Board (DRDO)", url: "https://drdo.gov.in/drdo/aeronautics-research-development" },
  { name: "NRB", full: "Naval Research Board (DRDO)", url: "https://drdo.gov.in/" },
];

const HIGHLIGHTS = [
  { icon: FileText, title: "Proposal, Paper & Thesis builders", body: "MS-Word-class rich editor with citations, tables, figures — and PDF / DOCX export you can hand to a committee." },
  { icon: Sparkles, title: "AI co-writer for every section", body: "Outline first, then draft section-by-section with Claude 4.6, GPT & Gemini — your voice stays intact." },
  { icon: Compass, title: "Live funding & travel discovery", body: "60+ agencies searchable by geography, discipline and keyword — refreshed every time you look." },
  { icon: Bell, title: "Watchlist alerts", body: "Google-Alerts-style keyword watchers deliver new calls straight to your dashboard." },
  { icon: ShieldCheck, title: "Plagiarism & consistency AI", body: "Upload a draft — get similarity flags, terminology drift and thesis-chapter coherence checks." },
  { icon: Mic, title: "Voice-to-Draft", body: "Dictate an idea, get a polished paragraph — powered by OpenAI Whisper." },
  { icon: BarChart3, title: "Analytics dashboard", body: "Track proposals in flight, submission rates, funder mix and personal writing cadence." },
  { icon: BookOpenCheck, title: "One reusable CV", body: "Publications, past grants, projects, collaborators — reused across every proposal you file." },
];

const TECH = [
  { icon: Braces, label: "React 19", detail: "Frontend" },
  { icon: Cpu, label: "FastAPI", detail: "Python backend" },
  { icon: Database, label: "MongoDB", detail: "Document store" },
  { icon: Sparkles, label: "Claude 4.6 · GPT · Gemini", detail: "AI co-writer" },
  { icon: Mic, label: "OpenAI Whisper", detail: "Voice-to-draft" },
  { icon: Layers, label: "TipTap · Shadcn UI", detail: "Editor & design" },
];

const DISCIPLINES = ["Engineering", "Medicine", "Law", "Life Sciences", "Physical Sciences", "Arts", "Commerce", "Social Sciences"];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen paper relative" data-testid="landing-page">
      <div className="grain absolute inset-0" />

      {/* Header */}
      <header className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-xl">G</div>
          <div>
            <div className="font-display text-2xl leading-none">GrantForge</div>
            <div className="overline mt-1">Est. 2026 · Global</div>
          </div>
        </div>
        <nav className="flex items-center gap-3">
          <a href="#highlights" className="hidden sm:inline text-sm hover:text-primary" data-testid="nav-highlights">Highlights</a>
          <a href="#stack" className="hidden sm:inline text-sm hover:text-primary" data-testid="nav-stack">Technology</a>
          <Link to="/login" className="text-sm hover:text-primary" data-testid="nav-signin">Sign in</Link>
          <Button
            onClick={() => nav("/login")}
            data-testid="nav-getstarted"
            className="rounded-full px-5"
          >
            Sign in to continue <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-6 pb-16">
        <div className="grid md:grid-cols-12 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="md:col-span-7 space-y-6"
          >
            <div className="overline">A workshop for research proposals</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Win the grant.<br />
              <span className="italic text-primary">Keep the wonder.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              A quiet, dignified studio for professors — from your first proposal to your fiftieth.
              Discover global calls, draft with an AI research writer, curate your team,
              and export a proposal that reads like you meant every word.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                size="lg"
                className="rounded-full px-7"
                onClick={() => nav("/login")}
                data-testid="hero-cta-signin"
              >
                Sign in to your studio <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a
                href="mailto:hello@grantforge.io?subject=GrantForge%20access%20request&body=Hello%20—%20please%20provision%20a%20GrantForge%20account.%0AName%3A%0AInstitution%3A%0ADiscipline%3A"
                data-testid="hero-cta-request"
              >
                <Button size="lg" variant="outline" className="rounded-full px-7">
                  <Mail className="w-4 h-4 mr-2" /> Request access from admin
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5 text-success"><ShieldCheck className="w-4 h-4" /> Invite-only · admin-provisioned</div>
              <div className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> Claude · GPT · Gemini</div>
              <div className="flex items-center gap-1.5"><Globe2 className="w-4 h-4 text-primary" /> 60+ agencies</div>
              <div className="flex items-center gap-1.5"><DownloadCloud className="w-4 h-4 text-primary" /> PDF · DOCX · XLSX</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-5 grid grid-rows-6 gap-4"
          >
            <div className="row-span-4 rounded-lg overflow-hidden border border-border relative">
              <img src={HERO_IMG} alt="University architecture" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="overline text-white/80">Featured</div>
                <div className="font-display text-2xl leading-tight">Draft like a fellow. Submit like a laureate.</div>
              </div>
            </div>
            <div className="row-span-2 rounded-lg border border-border bg-card p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center"><Award className="w-6 h-6" /></div>
              <div>
                <div className="overline">For every discipline</div>
                <div className="text-sm mt-1">Engineering · Medicine · Law · Sciences · Arts &amp; Commerce</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Purpose strip — deep navy bar echoing the reference */}
      <section className="relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className="rounded-2xl bg-accent text-accent-foreground px-6 md:px-10 py-8 md:py-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 shadow-[0_1px_0_0_hsl(var(--border))]"
            data-testid="purpose-strip"
          >
            <div className="md:flex-1">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/20 text-primary px-3 py-1 text-[11px] uppercase tracking-[0.22em] font-semibold">
                <Sparkles className="w-3.5 h-3.5" /> One studio · many works
              </div>
              <div className="overline text-white/70 mt-4">What GrantForge is</div>
              <div className="font-display text-3xl md:text-4xl leading-tight mt-2">
                An academic writing studio for working professors — 1 to 20 years in.
              </div>
              <div className="mt-3 text-sm md:text-base text-white/80 max-w-xl">
                Write, discover and defend — <span className="text-primary font-medium">proposals, papers, theses, travel grants and consultancy</span> all live in one place.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:min-w-[340px]">
              <div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Research Proposals</div>
              <div className="flex items-center gap-2"><BookOpenCheck className="w-4 h-4" /> Paper Publications</div>
              <div className="flex items-center gap-2"><Layers className="w-4 h-4" /> Theses</div>
              <div className="flex items-center gap-2"><Compass className="w-4 h-4" /> Funding calls</div>
              <div className="flex items-center gap-2"><Globe2 className="w-4 h-4" /> International Travel Grants</div>
              <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> AI coaches</div>
              <div className="flex items-center gap-2"><Search className="w-4 h-4" /> Plagiarism &amp; consistency</div>
            </div>
          </div>
        </div>
      </section>

      {/* Indian Agencies — scrolling ticker (click to open) */}
      <section className="relative z-10 pt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10 mb-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-success/15 text-success px-3 py-1 text-[11px] uppercase tracking-[0.22em] font-semibold">
              <Award className="w-3.5 h-3.5" /> India-first funding coverage
            </div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">
              200+ funding agencies · India and worldwide
            </h2>
            <p className="text-muted-foreground mt-2 text-base leading-relaxed max-w-2xl">
              Hover the ticker to pause. Click any agency to open its official portal in a new tab.
            </p>
          </div>
          <Link to="/login" className="text-sm text-primary hover:underline shrink-0" data-testid="indian-grid-explore">Open the Funding Feed →</Link>
        </div>
        <div className="marquee-fade border-y border-border bg-secondary/40 py-4">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {INDIAN_AGENCIES.map((a, i) => (
              <a
                key={`row1-${a.name}`}
                href={a.url}
                target="_blank"
                rel="noreferrer noopener"
                title={`${a.full} · ${a.url}`}
                data-testid={`indian-agency-${i}`}
                className="inline-flex items-center gap-3 mx-3 rounded-full border border-border bg-card px-5 py-2.5 hover:border-success/60 hover:bg-success/10 hover:text-success transition-colors"
              >
                <span className="font-display text-lg leading-none">{a.name}</span>
                <span className="text-xs text-muted-foreground max-w-[220px] truncate">{a.full}</span>
              </a>
            ))}
          </Marquee>
        </div>
        <div className="marquee-fade border-b border-border bg-secondary/20 py-4 mt-1">
          <Marquee gradient={false} speed={32} direction="right" pauseOnHover>
            {[...INDIAN_AGENCIES].reverse().map((a, i) => (
              <a
                key={`row2-${a.name}`}
                href={a.url}
                target="_blank"
                rel="noreferrer noopener"
                title={`${a.full} · ${a.url}`}
                data-testid={`indian-agency-r2-${i}`}
                className="inline-flex items-center gap-3 mx-3 rounded-full border border-border bg-card px-5 py-2.5 hover:border-success/60 hover:bg-success/10 hover:text-success transition-colors"
              >
                <span className="font-display text-lg leading-none">{a.name}</span>
                <span className="text-xs text-muted-foreground max-w-[220px] truncate">{a.full}</span>
              </a>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Marquee of international agencies */}
      <section className="relative z-10 py-10 mt-14 border-y border-border bg-secondary/40">
        <div className="overline text-center mb-3">And global paths · International funding agencies</div>
        <div className="marquee-fade">
          <Marquee gradient={false} speed={38} pauseOnHover>
            {AGENCIES.map((a, i) => (
              <span key={i} className="font-display text-2xl md:text-3xl mx-8 text-foreground/80">
                {a} <span className="text-primary/60 mx-3">·</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Highlights grid — full feature tour BEFORE login */}
      <section id="highlights" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-2xl mb-10">
          <div className="overline">Everything inside</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight mt-2">
            One studio. Every stage of the write.
          </h2>
          <p className="text-muted-foreground mt-3 text-base leading-relaxed">
            Discovery, drafting, review and submission — held together so nothing falls between the cracks.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {HIGHLIGHTS.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="rounded-lg border border-border p-5 bg-card hover:border-primary/50 transition-colors"
              data-testid={`highlight-${i}`}
            >
              <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center mb-3">
                <h.icon className="w-5 h-5" />
              </div>
              <div className="font-display text-xl leading-tight">{h.title}</div>
              <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{h.body}</div>
            </motion.div>
          ))}
        </div>

        {/* Discipline chips */}
        <div className="mt-10 flex flex-wrap items-center gap-2">
          <span className="overline mr-2">Built for</span>
          {DISCIPLINES.map((d) => (
            <span
              key={d}
              className="text-xs uppercase tracking-widest px-3 py-1 rounded-full border border-success/40 bg-success/10 text-success"
              data-testid={`discipline-${d.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {d}
            </span>
          ))}
        </div>
      </section>

      {/* Technology stack */}
      <section id="stack" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-12">
          <div className="grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <div className="overline">Built on</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight mt-2">
                Editorial craft.<br />
                <span className="italic text-primary">Modern rails.</span>
              </h2>
              <p className="text-muted-foreground mt-3 text-base leading-relaxed">
                Every part of the stack is chosen for a professor's workflow —
                fast to write in, safe to store your CV in, and precise when the AI helps.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
              {TECH.map((t, i) => (
                <div
                  key={t.label}
                  className="rounded-lg border border-border bg-background/60 p-4 flex items-start gap-3"
                  data-testid={`tech-${i}`}
                >
                  <div className="w-10 h-10 rounded-md bg-accent text-accent-foreground grid place-items-center">
                    <t.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{t.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Second image block */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div className="rounded-lg overflow-hidden border border-border relative min-h-[240px]">
          <img src={LIB_IMG} alt="library" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 text-white">
            <div className="overline text-white/80">You are not alone</div>
            <div className="font-display text-3xl">Whether it is your first proposal or your fiftieth.</div>
          </div>
        </div>
      </section>

      {/* Access strip */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div className="max-w-xl">
            <div className="overline">Access is by invitation</div>
            <div className="font-display text-3xl leading-tight mt-2">Already have credentials from your admin?</div>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              GrantForge is a curated, admin-provisioned platform. If you were onboarded by your department or by our team, sign in below. Otherwise email us and we'll set you up — usually within one business day.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              size="lg"
              className="rounded-full px-7"
              onClick={() => nav("/login")}
              data-testid="footer-cta-signin"
            >
              Sign in <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a
              href="mailto:hello@grantforge.io?subject=GrantForge%20access%20request"
              data-testid="footer-cta-request"
            >
              <Button size="lg" variant="outline" className="rounded-full px-7">
                <Mail className="w-4 h-4 mr-2" /> Contact admin
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-sm text-muted-foreground">
          <div>© 2026 GrantForge · An academic writing studio for professors, worldwide.</div>
          <div className="flex gap-5">
            <Link to="/login" className="hover:text-primary">Sign in</Link>
            <a href="mailto:hello@grantforge.io" className="hover:text-primary">Request access</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
